// Flashes the firmware images in fw/ (see tools/pack_firmware.mjs) to an ESP32 DevKit from the
// browser, with esptool-js.
//
// The DevKit's USB-UART bridge (CP210x, or CH340 on some clones) drives EN and GPIO0 with DTR/RTS,
// so esptool resets the chip into its bootloader and back by itself: no buttons. The bridge keeps
// the same USB identity before and after flashing, so one port permission covers everything.

import { assetUrl } from '../core/assets.js';
// The USB-UART bridges a DevKit may have: the same filter lists the board for connecting and for flashing.
export const PORT_FILTERS = [
  { usbVendorId: 0x10c4, usbProductId: 0xea60 }, // Silicon Labs CP210x
  { usbVendorId: 0x1a86, usbProductId: 0x7523 }, // WCH CH340
];
export const BOOTLOADER_FILTERS = PORT_FILTERS;

export async function loadImages(fetchFn = fetch, base = assetUrl('fw/')) {
  const get = async (name) => {
    const r = await fetchFn(base + name, { cache: 'no-cache' });
    if (!r.ok) throw new Error(`${base}${name} not found (${r.status}). Run "make firmware" in web/ and rebuild.`);
    return r;
  };
  const manifest = await (await get('manifest.json')).json();
  if (manifest.chip !== 'esp32') throw new Error(`firmware images are for ${manifest.chip}, not the ESP32`);
  const files = [];
  for (const f of manifest.files) files.push({ address: f.address, data: new Uint8Array(await (await get(f.file)).arrayBuffer()) });
  return { version: manifest.version, flash: manifest.flash, files };
}

// lib: the esptool-js module ({ESPLoader, Transport}); injected by tests.
// Resolves with the chip description. Always releases the port at the end.
// eraseSettings: erase the whole flash first (this includes the stored pin settings),
// so the device starts unconfigured; everything needed is written right after.
export async function flashFirmware({ port, lib, images, eraseSettings = false, onProgress = () => {}, onLog = () => {} }) {
  const files = images.files;
  const transport = new lib.Transport(port, false);
  const terminal = { clean() {}, writeLine: (s) => onLog(s), write: (s) => onLog(s) };
  try {
    const loader = new lib.ESPLoader({ transport, baudrate: 460800, romBaudrate: 115200, terminal });
    // 'default_reset': DTR/RTS put the chip into its bootloader through the board's auto-reset circuit.
    const chip = await loader.main('default_reset');
    if (!/^ESP32(?!-[SCHP]\d)/i.test(chip)) throw new Error(`This is not an ESP32 (${chip}).`);
    const total = files.reduce((n, f) => n + f.data.length, 0);
    const done = files.map(() => 0);
    await loader.writeFlash({
      fileArray: files,
      flashMode: images.flash.mode,
      flashFreq: images.flash.freq,
      flashSize: images.flash.size,
      eraseAll: eraseSettings, // otherwise the stored pin configuration (NVS) survives
      compress: true,
      reportProgress: (i, written, size) => {
        done[i] = (written / size) * files[i].data.length;
        onProgress(Math.min(1, done.reduce((a, b) => a + b, 0) / total));
      },
    });
    onProgress(1);
    await loader.after('hard_reset');
    return chip;
  } finally {
    try {
      await transport.disconnect();
    } catch {
      // the chip is already rebooting; the port went away
    }
  }
}
